import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { AuthLayout, FormError } from '@/components/auth/AuthLayout';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'https://api.latlens.com';

interface Request { child_name: string; child_email: string; expires_at: number }
type State =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'open'; request: Request }
  | { kind: 'granted' }
  | { kind: 'refused' };

// Where a parent or guardian lands from the permission email. Opening the
// link only shows the request (mail scanners open links); the decision is a
// button press.
const GuardianConsentPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('t');
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [name, setName] = useState('');
  const [isGuardian, setIsGuardian] = useState(false);
  const [confirmRefuse, setConfirmRefuse] = useState(false);
  const [problems, setProblems] = useState<{ name?: string; guardian?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/auth/guardian-consent?t=${encodeURIComponent(token)}`)
      .then(async (res) => setState(res.ok ? { kind: 'open', request: await res.json() } : { kind: 'invalid' }))
      .catch(() => setState({ kind: 'invalid' }));
  }, [token]);

  const decide = async (body: object) => {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/auth/guardian-consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, ...body }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 404) return setState({ kind: 'invalid' });
      if (!res.ok) return setError(data.error ?? 'Something went wrong. Try again.');
      setState({ kind: data.status === 'granted' ? 'granted' : 'refused' });
    } catch {
      setError("We couldn't reach Latlens. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const grant = () => {
    const next = {
      name: name.trim().length < 2 ? 'Enter your full name' : undefined,
      guardian: isGuardian ? undefined : "Confirm you're their parent or legal guardian",
    };
    setProblems(next);
    if (next.name || next.guardian) return;
    void decide({ decision: 'grant', guardian_name: name.trim(), is_guardian: true });
  };

  if (!token) {
    return <AuthLayout title="This link is incomplete" subtitle="Open the link from the email again." ><span /></AuthLayout>;
  }
  if (state.kind === 'loading') return <AuthLayout title="Permission request"><p className="text-sm text-muted-foreground">Loading…</p></AuthLayout>;
  if (state.kind === 'invalid') {
    return (
      <AuthLayout title="This link isn't valid any more"
        subtitle="It may have expired, or someone already answered it. If the young person still wants an account, they can sign up again and give your email address.">
        <span />
      </AuthLayout>
    );
  }
  if (state.kind === 'granted') {
    return <AuthLayout title="Permission given" subtitle="Thank you. We've told them they can sign in now. You can withdraw permission at any time by writing to privacy@latlens.com."><span /></AuthLayout>;
  }
  if (state.kind === 'refused') {
    return <AuthLayout title="Account deleted" subtitle="We deleted the account and the details they gave us. We've let them know."><span /></AuthLayout>;
  }

  const { child_name, child_email } = state.request;
  return (
    <AuthLayout title="Your permission is needed"
      subtitle="Someone under 18 asked to create a Latlens account and gave your email address as their parent or guardian.">
      <div className="space-y-6">
        <dl className="divide-y divide-border rounded-xl border border-border bg-surface text-sm">
          <div className="flex justify-between gap-4 px-4 py-3"><dt className="text-muted-foreground">Name</dt><dd className="font-medium text-foreground">{child_name}</dd></div>
          <div className="flex justify-between gap-4 px-4 py-3"><dt className="text-muted-foreground">Email</dt><dd className="break-all font-medium text-foreground">{child_email}</dd></div>
        </dl>
        <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
          <p>Latlens makes maps, search and directions for Africa. The account holds their name, email address and date of birth, and what they choose to save or send while using it. Read the <a href="/privacy" className="text-accent-text hover:underline">Privacy Policy</a>.</p>
          <p>If you give permission, they can sign in straight away. You can withdraw it later by writing to privacy@latlens.com.</p>
        </div>

        {confirmRefuse ? (
          <div className="space-y-3">
            <p className="text-sm text-foreground">We'll delete the account and the details {child_name} gave us. This can't be undone.</p>
            <FormError message={error} />
            <div className="flex flex-wrap gap-3">
              <Button type="button" variant="danger" loading={busy} onClick={() => decide({ decision: 'refuse' })}>Yes, delete the account</Button>
              <Button type="button" variant="ghost" onClick={() => setConfirmRefuse(false)}>Go back</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <Input label="Your full name" autoComplete="name" value={name} error={problems.name} onChange={(e) => setName(e.currentTarget.value)} />
            <div>
              <label className="flex items-start gap-2.5 text-sm text-foreground">
                <input type="checkbox" checked={isGuardian} onChange={(e) => setIsGuardian(e.currentTarget.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border accent-[rgb(var(--color-accent))]" />
                <span>I'm {child_name}'s parent or legal guardian</span>
              </label>
              {problems.guardian && <p className="mt-1.5 text-sm text-error">{problems.guardian}</p>}
            </div>
            <FormError message={error} />
            <div className="flex flex-wrap gap-3">
              <Button type="button" size="lg" loading={busy} onClick={grant}>Give permission</Button>
              <Button type="button" size="lg" variant="ghost" onClick={() => setConfirmRefuse(true)}>Don't give permission</Button>
            </div>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export default GuardianConsentPage;
